import React, { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { uploadFiles, editFile, renameFile } from '../../../api/api'; // Replace with appropriate API calls
import { toast } from 'react-toastify';
import { useTheme } from '../../../ThemeContext';
const AddFiles = ({ isOpen, onClose, main, update, project_id, editing, fileDetails }) => {
  const {theme} =useTheme()
  const isDark = theme === 'dark';
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [description, setDescription] = useState('');
  const [action, setAction] = useState(''); // Tracks the current action: 'rename' or 'update'

  // Populate fields if in editing mode
  useEffect(() => {
    if (editing && fileDetails) {
      setFileName(fileDetails.fileName || ''); // Pre-fill file name
      setDescription(fileDetails.description || ''); // Pre-fill description, if available
    } else {
      setFile(null);
      setFileName('');
      setDescription('');
    }
  }, [editing, fileDetails]);

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  // Handle save for Rename or Update
  const handleSave = async (e) => {
    e.preventDefault();

    try {
      if (action === 'rename') {
        // Logic for renaming
        const data = {
          fileId: fileDetails?._id,
          newFileName: fileName,
        };

        const response = await renameFile(data);
        toast.success('File renamed successfully!');
      } else if (action === 'update') {
        // Logic for updating file and description
        const formData = new FormData();
        formData.append('fileId', fileDetails?._id);
        formData.append('description', description);
        formData.append('file', file);

        const response = await editFile(formData);
        toast.success('File updated successfully!');
      } else {
        // Logic for adding a new file
        const formData = new FormData();
        formData.append('folderPath', main);
        formData.append('fileName', fileName);
        formData.append('projectId', project_id);
        formData.append('file', file);

        const response = await uploadFiles(formData);
        toast.success('File added successfully!');
      }
      update(); // Refresh parent component
      onClose(); // Close modal
    } catch (error) {
      toast.error(error.response?.data?.message || 'An error occurred, please try again.');
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900 mb-4">
                  {editing ? 'Edit File' : 'Add File'}
                </Dialog.Title>
                <form onSubmit={handleSave} className="space-y-4">
                  {editing ? (
                    <>
                      {/* Buttons for Rename or Update */}
                      <div className="flex space-x-4">
                        <button
                          type="button"
                          onClick={() => setAction('rename')}
                          className={`px-4 py-2 text-sm font-medium rounded-md ${
                            action === 'rename' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          onClick={() => setAction('update')}
                          className={`px-4 py-2 text-sm font-medium rounded-md ${
                            action === 'update' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          Update
                        </button>
                      </div>

                      {/* Rename Mode */}
                      {action === 'rename' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">File Name</label>
                          <input
                            type="text"
                            value={fileName}
                            onChange={(e) => setFileName(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>
                      )}

                      {/* Update Mode */}
                      {action === 'update' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">File Name</label>
                            <input
                              type="text"
                              value={fileName}
                              disabled
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 cursor-not-allowed"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Upload New File</label>
                            <input
                              type="file"
                              onChange={handleFileChange}
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <textarea
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    // Add Mode
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">File Name</label>
                        <input
                          type="text"
                          value={fileName}
                          onChange={(e) => setFileName(e.target.value)}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Upload File</label>
                        <input
                          type="file"
                          onChange={handleFileChange}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </>
                  )}

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700`}
                    >
                      {action === 'rename' ? 'Rename' : action === 'update' ? 'Save' : 'Add File'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddFiles;
